import { css } from "@/domains/emotion";
import styled from "@emotion/styled";
import {
  MdsMenuItemSize,
  MdsMenuItemProps,
  MdsMenuItemSelectionVariant,
} from "@/design-system/components/menu-item/types";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { MdsShortcutKey } from "@/design-system/components/shortcut-key/MdsShortcutKey";
import { MdsShortcutKeyCharacter } from "@/design-system/components/shortcut-key/types";
import { useTheme } from "@emotion/react";

// Figma Link: https://www.figma.com/design/ZEgqtUJHh5ksFWQ7Nq0P2u/Mem-Design-System?node-id=7335-32433&m=dev

const iconSizeStyleMapping: { [key in MdsMenuItemSize]: string } = {
  [MdsMenuItemSize.Small]: css({
    height: 11,
  }),
  [MdsMenuItemSize.Medium]: css({
    height: 32,
  }),
};

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const MdsMenuItem = ({
  className,
  size,
  title,
  subtitle,
  icon,
  centerIcon,
  isSelected,
  selectionVariant = MdsMenuItemSelectionVariant.Checkmark,
  isShared,
  rightLabel,
  shortcutKeys,
  onClick,
}: MdsMenuItemProps) => {
  const showSubtitle = size === MdsMenuItemSize.Medium && subtitle;
  return (
    <Wrapper size={size} onClick={onClick} className={className}>
      <WrapperLeft>
        <WrapperIcons>
          <MdsMenuItemIcon icon={icon} size={size} />
          {!centerIcon && showSubtitle && <SubtitleHeight />}
        </WrapperIcons>
        <WrapperText>
          <WrapperTextTop>
            <Title>{title}</Title>
            {isShared && (
              <MdsIcon
                kind={MdsIconKind.Shared}
                innerStyles={{ Icon: { className: SharedIconStyles } }}
              />
            )}
          </WrapperTextTop>
          {showSubtitle && <Subtitle>{subtitle}</Subtitle>}
        </WrapperText>
      </WrapperLeft>
      <WrapperRight>
        <MdsMenuItemSelected isSelected={isSelected} selectionVariant={selectionVariant} />
        <MdsMenuItemRightLabel rightLabel={rightLabel} />
        <MdsMenuItemShortcutKeys shortcutKeys={shortcutKeys} />
      </WrapperRight>
    </Wrapper>
  );
};

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const MdsMenuItemIcon = ({
  icon,
  size,
}: {
  icon: MdsMenuItemProps["icon"];
  size: MdsMenuItemSize;
}) => {
  const iconSize = size === MdsMenuItemSize.Small ? 11 : 32;
  if (!icon) return null;
  const iconContent =
    typeof icon === "function" ? (
      icon(iconSize)
    ) : typeof icon === "string" ? (
      <MdsIcon
        kind={icon as MdsIconKind}
        innerStyles={{ Icon: { className: iconSizeStyleMapping[size] } }}
      />
    ) : (
      icon
    );
  return <WrapperIcon>{iconContent}</WrapperIcon>;
};

const MdsMenuItemShortcutKeys = ({
  shortcutKeys,
}: {
  shortcutKeys?: MdsShortcutKeyCharacter[];
}) => {
  if (!shortcutKeys) return null;
  return (
    <ShortcutKeysWrapper>
      {shortcutKeys.map(key => (
        <MdsShortcutKey key={key} character={key} />
      ))}
    </ShortcutKeysWrapper>
  );
};

const MdsMenuItemRightLabel = ({ rightLabel }: { rightLabel?: string }) => {
  if (!rightLabel) return null;
  return <RightLabel>{rightLabel}</RightLabel>;
};

const MdsMenuItemSelected = ({
  isSelected,
  selectionVariant,
}: {
  isSelected?: boolean;
  selectionVariant: MdsMenuItemSelectionVariant;
}) => {
  const theme = useTheme();
  if (isSelected === undefined) return null;
  if (selectionVariant === MdsMenuItemSelectionVariant.Checkmark) {
    if (isSelected)
      return (
        <MdsIcon
          kind={MdsIconKind.Check}
          innerStyles={{ Icon: { className: css({ color: theme.colors.icon.selected }) } }}
        />
      );
    return null;
  }
  if (selectionVariant === MdsMenuItemSelectionVariant.RadioButton) {
    if (isSelected)
      return (
        <MdsIcon
          kind={MdsIconKind.CheckCircleSolid}
          innerStyles={{ Icon: { className: css({ color: theme.colors.icon.selected }) } }}
        />
      );
    if (!isSelected)
      return (
        <MdsIcon
          kind={MdsIconKind.Circle}
          innerStyles={{ Icon: { className: css({ color: theme.colors.icon.secondary }) } }}
        />
      );
  }
  return null;
};

const Wrapper = styled.button<{ size: MdsMenuItemSize }>(({ theme, size }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  height: size === MdsMenuItemSize.Small ? "36px" : "54px",
  width: "100%",
  minWidth: "176px",
  maxWidth: "320px",
  paddingInline: theme.spacing["5"],
  paddingBlock: theme.spacing["2"],
  "&:hover": {
    backgroundColor: theme.colors.background.menu.hover,
  },
}));

const WrapperLeft = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing.sm,
  overflow: "hidden",
  minWidth: "fit-content",
}));

const WrapperRight = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  paddingLeft: theme.spacing["2"],
  gap: theme.spacing.sm,
  flexShrink: 0,
}));

const WrapperIcon = styled.div(() => ({
  height: "100%",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  flexShrink: 0,
}));

const WrapperText = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  fontSize: theme.fontSizes.small,
  gap: theme.spacing["0.5"],
  overflow: "hidden",
  minWidth: 0,
}));

const WrapperIcons = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.spacing["0.5"],
  overflow: "hidden",
  height: "100%",
  minWidth: 0,
}));

const WrapperTextTop = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: theme.spacing.sm,
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  width: "100%",
}));

const Title = styled.div(({ theme }) => ({
  fontSize: theme.fontSizes.small,
  lineHeight: theme.lineHeights.small,
  textAlign: "left",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  width: "100%",
}));

const SubtitleHeight = styled.div(({ theme }) => ({
  height: theme.lineHeights.xsmall,
}));

const Subtitle = styled.span(({ theme }) => ({
  fontSize: theme.fontSizes.xxsmall,
  lineHeight: theme.lineHeights.xsmall,
  color: theme.colors.grey.x500,
  textAlign: "left",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  width: "100%",
}));

const RightLabel = styled.span(({ theme }) => ({
  fontSize: theme.fontSizes.small,
  lineHeight: theme.lineHeights.xsmall,
  color: theme.colors.grey.x500,
}));

const ShortcutKeysWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing.xs,
}));

const SharedIconStyles = css({
  height: 12,
  width: 12,
});
